import { useEffect, useState } from 'react';
import { ArrowTopRightIcon, SewingPinFilledIcon } from '@radix-ui/react-icons';
import { Button, Spinner } from '@radix-ui/themes';
import { useNavigate } from 'react-router-dom';
import { isCurrentUserPremium, isLoggedIn, userIsFitPlayerProfile } from 'helpers/authentication';
import { pushEventWithProperties } from 'helpers/crmUtils';
import { getNextRank, getPreviousRank, openInExternalUrl } from 'helpers/utils';
import Analytics from 'ui/design-base/Analytics';
import { clearHarmonizedData, fetchHarmonizedRanking } from 'store/playerProfile/playerProfileSlice';
import { useAppDispatch, useAppSelector } from 'store/store';
import type { Rank } from 'types/player';
import ModalNoCard from './ModalNoCard';

export const HarmonizedRankExplanation: React.FC<{
  currentRank?: Rank;
  playerId?: number;
}> = ({ currentRank, playerId }) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [modalNoCardOpen, setModalNoCardOpen] = useState(false);
  const { loadingHarmonization, harmonizedData } = useAppSelector((state) => state.playerProfile);

  useEffect(
    () => () => {
      dispatch(clearHarmonizedData());
    },
    [dispatch],
  );

  useEffect(() => {
    if (isCurrentUserPremium()) {
      pushEventWithProperties('TT - Premium Usage', {
        type: 'armonizzata',
      });
    } else {
      pushEventWithProperties('TT - Paywall', {
        type: 'armonizzata',
      });
    }
  }, []);

  const onClickHarmonizedButton = () => {
    Analytics.sendEvent({
      category: 'Profile Interaction',
      action: 'Rank Harmonization Button',
      label: 'Player Profile',
    });
    if (!isLoggedIn()) navigate('/accedi');
    if (userIsFitPlayerProfile()) {
      dispatch(fetchHarmonizedRanking(playerId));
    } else {
      setModalNoCardOpen(true);
    }
  };

  const getPercentage = () => {
    if (!harmonizedData) return 0;
    const percentage =
      (harmonizedData?.position - harmonizedData?.lower_rank_threshold) /
      (harmonizedData?.upper_rank_threshold - harmonizedData?.lower_rank_threshold);
    //rescalate from 31 to 63
    return Math.round(31 + percentage * 32);
  };

  return (
    <div id='container' className='flex max-w-96 grow-0 basis-96 flex-col items-start gap-4 rounded-xl p-3'>
      <div id='content' className='flex flex-col items-start gap-4 self-stretch'>
        <div id='graph' className='relative w-full items-center self-stretch'>
          {getPercentage() !== 0 && (
            <SewingPinFilledIcon
              height='16'
              width='16'
              color='red'
              style={{
                marginLeft: `${getPercentage()}%`,
              }}
            />
          )}
          {loadingHarmonization && <Spinner />}
          <div id='graph' className='flex w-full items-center self-stretch'>
            <div id='prev' className='flex w-1/3 flex-col items-center gap-1'>
              <div
                id='bar'
                className='border-r-orange-8 bg-orange-5 h-2 self-stretch border border-r-4 border-solid border-transparent'
              />
              <div id='prevRank' className='text-neutral-neutral-8 text-base font-medium'>
                {harmonizedData?.rank ? getPreviousRank(harmonizedData?.rank) : getPreviousRank(currentRank)}
              </div>
            </div>
            <div id='current' className='flex w-1/3 flex-col items-center gap-1'>
              <div
                id='bar'
                className='border-r-neutral-neutral-8 bg-neutral-neutral-5 h-2 self-stretch border border-r-4 border-solid border-transparent'
              />
              <div id='currentRank' className='text-neutral-neutral-8 text-base font-bold'>
                {harmonizedData?.rank ? harmonizedData?.rank : currentRank ? currentRank : '-'}
              </div>
            </div>
            <div id='next' className='flex w-1/3 flex-col items-center gap-1'>
              <div
                id='bar'
                className='border-r-green-8 bg-green-5 h-2 self-stretch border border-r-4 border-solid border-transparent'
              />
              <div id='nextRank' className='text-neutral-neutral-8 text-base font-medium'>
                {harmonizedData?.rank ? getNextRank(harmonizedData?.rank) : getNextRank(currentRank)}
              </div>
            </div>
          </div>
        </div>
        <div id='legenda' className='text-neutral-neutral-12 flex items-center gap-2 self-stretch text-sm font-medium'>
          <SewingPinFilledIcon height='16' width='16' color='red' />{' '}
          {harmonizedData?.rank ? 'La tua ' : 'Scopri la tua '}
          posizione attuale armonizzata
        </div>

        <div className='text-neutral-neutral-12 text-sm font-normal text-wrap'>
          {harmonizedData ? (
            <>
              <li>
                La classifica armonizzata del giocatore ad oggi è: <b>{harmonizedData?.rank}</b>
              </li>
              {harmonizedData?.rank !== 'ND' ? (
                <>
                  <li>
                    Questo perchè il giocatore è nella posizione: <b>{harmonizedData?.position}</b> su un totale
                    di&nbsp;
                    <b>{harmonizedData?.total_players}</b> atleti tesserati attivi quest&apos;anno
                  </li>

                  <li>
                    La classifica di <b>{harmonizedData?.rank}</b> comprenderà a fine anno i giocatori tra la
                    posizione&nbsp;
                    <b>{harmonizedData?.upper_rank_threshold || harmonizedData?.position}</b> e la posizione&nbsp;
                    <b>{harmonizedData?.lower_rank_threshold || harmonizedData?.position}</b>
                  </li>
                  {harmonizedData?.position &&
                    harmonizedData?.lower_rank_threshold &&
                    harmonizedData?.upper_rank_threshold &&
                    (harmonizedData.position > harmonizedData.lower_rank_threshold ||
                      harmonizedData.position < harmonizedData.upper_rank_threshold) && (
                      <li>
                        <strong>
                          Nota: La posizione del giocatore è al di fuori delle fasce di classifica previste per questa
                          classifica. In un anno però non si può retrocedere di più di 1 classifica rispetto a quella di
                          inizio anno.
                        </strong>
                      </li>
                    )}
                  <p>
                    <br />
                  </p>
                  <p>
                    <em>
                      Ricordiamo che questa classifica non è definitiva, non dipende solo dal giocatore, e potrebbe
                      mutare in ogni momento, poichè dipende dall&apos;andamento di tutti i giocatori tesserati
                      d&apos;Italia
                    </em>
                  </p>
                </>
              ) : (
                <li>Perchè il tesseramento per questo giocatore non è attualmente attivo.</li>
              )}
            </>
          ) : (
            <>
              È una simulazione della classifica che il giocatore otterrà a fine anno. Ordina tutti i giocatori secondo
              la loro classifica simulata e punti FITP per poi, in base alle soglie definite nel metodo classifiche,
              stimare la classifica che si otterrà.
            </>
          )}
        </div>
      </div>
      {isCurrentUserPremium() ? (
        <div
          id='membership'
          className='border-accent-accent-8 bg-accent-accent-4 flex flex-col flex-wrap items-center justify-center gap-2 rounded-xl border border-solid p-3'
        >
          <div id='text' className='text-center text-base font-medium'>
            Hai accesso a questa funzionalità in quanto TennisTalker Club Member.
          </div>
          <div id='activate' className='w-full self-stretch'>
            <Button
              size='2'
              variant='outline'
              className='text-neutral-neutral-11 flex w-full items-center self-stretch p-6 hover:cursor-pointer'
              onClick={onClickHarmonizedButton}
            >
              Calcola la classifica armonizzata
            </Button>
          </div>
        </div>
      ) : (
        <div
          id='membership'
          className='border-accent-accent-8 bg-accent-accent-4 flex flex-col flex-wrap items-center justify-center gap-2 rounded-xl border border-solid p-3'
        >
          <div id='text' className='text-center text-base font-medium'>
            Funzione disponibile solo per i Membri TennisTalker.
          </div>
          <div id='activate' className='w-full self-stretch'>
            <Button
              size='2'
              variant='outline'
              className='text-neutral-neutral-11 flex w-full items-center self-stretch hover:cursor-pointer'
              onClick={() => navigate('/club-membership')}
            >
              Attiva membership <ArrowTopRightIcon />
            </Button>
          </div>
        </div>
      )}
      <div
        className='text-neutral-neutral-11 flex w-full items-center self-stretch underline hover:cursor-pointer hover:bg-transparent hover:underline-offset-2'
        onClick={() =>
          openInExternalUrl(
            'https://magazine.tennistalker.it/2025/09/10/la-classifica-fitp-armonizzata-cose-il-calcolo-solo-su-tennistalker/',
          )
        }
      >
        Articolo di dettaglio Classifica Armonizzata <ArrowTopRightIcon />
      </div>
      <ModalNoCard
        text='Per poter vedere la classifica armonizzata aggiornata ad oggi'
        open={modalNoCardOpen}
        setOpen={setModalNoCardOpen}
      />
    </div>
  );
};

export default HarmonizedRankExplanation;
